import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { useRevenueDeepDiveModalQuery } from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import {
  CustomIcons,
  PrintButton
} from 'modules/components/layer00';
import { ChatBubble } from 'modules/components/layer02';
import { CalculationMetricTable } from 'modules/components/layer25';
import { CompanyRevenueBreakdownSection } from 'modules/components/layer35';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';



type RevenueDeepDiveModalProps = {
  onClose?: () => void;
};

export const FC = ({
  onClose
}: RevenueDeepDiveModalProps) => {
  const { companyId } = useParams();
  const queryTuple = useRevenueDeepDiveModalQuery({
    skip: !companyId,
    variables: {
      companyId: companyId ?? ''
    }
  });

  const listedCompany = queryTuple.data?.listedCompany;
  const deepDiveTopics = listedCompany?.revenueDeepDive;
  const { topics, showAnalyzingState } = useMemo(() => {
    const topics = new Map<string, NonNullable<(typeof deepDiveTopics)>[number]>();
    deepDiveTopics?.forEach((t) => topics.set(t.topicName, t));
    const hasMissingCalculations = deepDiveTopics?.some((t) => t.missingCalculations.length > 0);

    const latestTopicUpdate = _.maxBy(deepDiveTopics, (t) => t.updatedTimestamp.toMillis());
    const updatedTimestamp = latestTopicUpdate?.updatedTimestamp ?? DateTime.now();
    const deadline = DateTime.now().minus({
      days: 1
    });

    return {
      topics,
      showAnalyzingState: hasMissingCalculations && updatedTimestamp.toMillis() > deadline.toMillis()
    };
  }, [deepDiveTopics]);

  if (!companyId) {
    return null;
  }



  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
    >
      <DialogTitle>
        <Stack
          width='100%'
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <CustomIcons.LargeIcon.FC icon='COMPANYLOGOFULL' />

          <PrintButton.FC />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Typography variant='activityHeader'>
            Revenue Deep Dive - {listedCompany?.ticker}
          </Typography>

          {showAnalyzingState && (
            <Box>
              <ChatBubble.FC
                role='SHERLOCK'
                text='I am still analyzing the most recent data. Take a look at my analysis so far and check back later for additional insights!'
              />
            </Box>
          )}
        </Box>

        <Grid
          container
          rowSpacing={4}
          columnSpacing={2}
        >
          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Abstract
            </Typography>
            <Divider />
          </Grid>

          <Grid item md={4}>
            <Typography variant='formLabel'>
              {topics.get('abstract')?.content}
            </Typography>
          </Grid>

          <Grid item md={8}>
            <CompanyRevenueBreakdownSection.FC listedCompanyId={companyId} />

          </Grid>
          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Core Growth Rates
            </Typography>

            <Divider />
          </Grid>

          <Grid
            item
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {topics.get('coreGrowthRates')?.content
              .split('\n\n')
              .map((v, i) => (
                <Typography
                  key={i}
                  variant='formLabel'
                >
                  {v}
                </Typography>
              ))}
          </Grid>

          <Grid item md={6}>
            <CalculationMetricTable.FC
              disableCalculations={showAnalyzingState ? topics.get('coreGrowthRates')?.missingCalculations : []}
              calculations={[
                'totalRevenueGrowthYoY',
                'totalRevenueGrowthQYoY',
                'totalRevenue_cagr',
                'totalRevenueGrowthYoY_fiveYearCAGR_difference',
                'totalRevenueGrowthYoY_derivative',
                'totalRevenueGrowthQYoY_derivative'
              ]}
            />
          </Grid>



          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Revenue Quality and Sustainability
            </Typography>

            <Divider />
          </Grid>

          <Grid item md={6}>
            <Typography variant='formLabel'>
              {topics.get('revenueQualityAndSustainability')?.content}
            </Typography>
          </Grid>

          <Grid item md={6}>
            <CalculationMetricTable.FC
              disableCalculations={showAnalyzingState ? topics.get('revenueQualityAndSustainability')?.missingCalculations : []}
              calculations={[
                'deferredRevenueGrowthQoQ_totalRevenueGrowthQoQ_difference',
                'deferredRevenueGrowthQYoY',
                'deferredRevenueGrowthQYoY_derivative',
                'deferredRevenueGrowthQYoY_totalRevenueGrowthQYoY_difference',
                'deferredRevenueGrowthYoY',
                'deferredRevenueGrowthYoY_derivative',
                'deferredRevenueGrowthYoY_totalRevenueGrowthYoY_difference',
                'accountsReceivableGrowthQYoY_totalRevenueGrowthQYoY_difference',
                'accountsReceivableGrowthYoY_totalRevenueGrowthYoY_difference',
                'workingCapitalConversionGrowthQYoY',
                'workingCapitalConversionGrowthYoY',
                'workingCapitalTrendConversionQYoY',
                'workingCapitalTrendConversionQYoY_derivative',
                'workingCapitalTrendConversionYoY',
                'workingCapitalTrendConversionYoY_derivative',
                'accrualRatioConversionGrowthQYoY',
                'accrualRatioConversionGrowthYoY',
                'accrualRatioConversionQoQ',
                'accrualRatioConversionYoY'
              ]}
            />
          </Grid>



          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Cost Structure Alignment
            </Typography>

            <Divider />
          </Grid>

          <Grid item md={6}>
            <Typography variant='formLabel'>
              {topics.get('costStructureAlignment')?.content}
            </Typography>
          </Grid>

          <Grid item md={6}>
            <CalculationMetricTable.FC
              disableCalculations={showAnalyzingState ? topics.get('costStructureAlignment')?.missingCalculations : []}
              calculations={[
                'totalOperatingExpenseGrowthQoQ_totalRevenueGrowthQoQ_difference',
                'totalOperatingExpenseGrowthQYoY_totalRevenueGrowthQYoY_difference',
                'totalOperatingExpenseGrowthYoY_totalRevenueGrowthYoY_difference',
                'grossMarginGrowthQYoY',
                'grossMarginGrowthQYoY_derivative',
                'ebitdaMarginGrowthQYoY',
                'ebitdaMarginGrowthQYoY_derivative',
                'netMarginConversionGrowthQYoY',
                'netMarginConversionGrowthQYoY_derivative'
              ]}
            />
          </Grid>



          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Valuation Context
            </Typography>

            <Divider />
          </Grid>

          <Grid
            item
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {topics.get('valuationContext')?.content
              .split('\n')
              .map((v, i) => (
                <Typography
                  key={i}
                  variant='formLabel'
                >
                  {v}
                </Typography>
              ))}
          </Grid>

          <Grid item md={6}>
            <CalculationMetricTable.FC
              disableCalculations={showAnalyzingState ? topics.get('valuationContext')?.missingCalculations : []}
              calculations={[
                'priceToEarnings_fiveYearAverage',
                'priceToEarningsRatioJustification',
                'enterpriseValueToEbitdaConversion_fiveYearAverage',
                'enterpriseValueToEbitdaConversionFY_fiveYearAverage_difference',
                'priceToBook_fiveYearAverage_difference'
              ]}
            />
          </Grid>



          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Cash Conversion and Earnings Quality
            </Typography>

            <Divider />
          </Grid>

          <Grid item md={6}>
            <Typography variant='formLabel'>
              {topics.get('cashConversionAndEarningsQuality')?.content}
            </Typography>
          </Grid>

          <Grid item md={6}>
            <CalculationMetricTable.FC
              disableCalculations={showAnalyzingState ? topics.get('cashConversionAndEarningsQuality')?.missingCalculations : []}
              calculations={[
                'freeCashFlowConversionGrowthQYoY',
                'freeCashFlowConversionGrowthQYoY_derivative',
                'freeCashFlowConversionGrowthYoY',
                'freeCashFlowConversionGrowthYoY_derivative',
                'netIncomeGrowthQYoY_operatingIncomeGrowthQYoY_difference',
                'netIncomeGrowthYoY_operatingIncomeGrowthYoY_difference',
                'returnOnInvestedCapitalQYoY_difference',
                'returnOnInvestedCapitalYoY_difference'
              ]}
            />
          </Grid>

          <Grid item md={12}>
            <Typography variant='activitySubHeaderPrimary'>
              Further Research
            </Typography>
            <Divider />
            <Typography variant='formLabel'>
              {topics.get('furtherResearch')?.content}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
